import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
// eslint-disable-next-line import/no-unresolved
import { EcheqRulesList, ModalWarn } from "@ui-kit/components/index.es";
import classNames from "classnames";
import { downloadErrorsEcheqRules } from "middleware/echeqRule";
import moment from "moment-timezone";
import Button from "pages/_components/Button";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import Image from "pages/_components/Image";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { push } from "react-router-redux";
import { actions as echeqRuleActions, selectors as echeqRuleSelectors } from "reducers/echeqRule";
import { actions, INITIAL_STATE, selectors } from "reducers/echeqRules";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { getAccountDescriptionLimited } from "util/accounts";
import { getAccountNumber, getCurrencyIso, getProductType } from "util/cbuCmf";
import * as configUtils from "util/config";
import { getLimitDate } from "util/date";
import { downloadXls } from "util/download";
import * as i18n from "util/i18n";
import Yup from "yup";
import { getPostLoginHomePath } from "util/v3UxConfig";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";

const FORM_ID = "echeqs.rule.list";
const RULE_MANAGE_PERMISSION = "rule.manage.echeq";

function EcheqRulesMain(props) {
    const {
        dispatch,
        isDesktop,
        rules,
        pagination,
        isFetching,
        processText,
        activeEnvironment,
        conditions,
        subConditionsAmount,
        subConditionsIssuer,
        subConditionsReceived,
        echeqsWithError,
        fetchingErrors,
        errorsPagination,
        activeSection,
        filters,
        goBackEcheqsRule,
        orderingRules,
        rememberedValues,
        embeddedInV3Shell,
    } = props;

    const [isModalWarnVisible, setIsModalWarnVisible] = useState(false);
    const [titleModalWarn, setTitleModalWarn] = useState("");
    const [descriptionModalWarn, setDescriptionModalWarn] = useState("");
    const [actionSelected, setActionSelected] = useState(null);
    const [idRuleSelected, setIdRuleSelected] = useState(null);
    const [currActiveSection, setCurrActiveSection] = useState(null);
    const [orderedList, setOrderedList] = useState(null);
    const rulesPrioritization = configUtils.getBoolean(
        "frontend.show.echeqRules.rulesPrioritization.functionalities",
        false,
    );
    const isEnabledSelectAll = configUtils.getBoolean(
        "frontend.show.echeqRules.contactsMassiveSelection.functionalities",
        false,
    );

    const getCreationDates = () => {
        let creationDates = {};
        if (!rememberedValues) {
            creationDates =
                rulesPrioritization && activeSection === "actives.inactives"
                    ? { creationDates: [null, null] }
                    : { creationDates: [getLimitDate(-1, new Date(), "months").toDate(), getLimitDate(0).toDate()] };
        }
        return creationDates;
    };

    useEffect(() => {
        dispatch(echeqRuleActions.echeqRuleCleanData());
        dispatch(
            actions.requestRulesList(
                {
                    ...(rememberedValues ? rememberedValues[activeSection] : filters[activeSection]),
                    activeSection,
                    ...getCreationDates(),
                    ...(goBackEcheqsRule ? { pageNumber: 1 } : {}),
                },
                isDesktop,
            ),
        );
        dispatch(echeqRuleActions.setGoBackEcheqsRule(false));
        setCurrActiveSection(activeSection);
    }, []);

    const getAccountFromCBU = (cbu) => {
        const number = getAccountNumber(cbu);
        const productType = getProductType(cbu);
        const currency = getCurrencyIso(cbu);
        return getAccountDescriptionLimited({ number, productType, currency });
    };

    const renderMapedConditions = (ruleConditions) =>
        (ruleConditions || []).map((item) => ({
            type: item.condition,
            operator: item.subCondition,
            value: item.value,
        }));

    const renderMapedRules = () => {
        if (!isDesktop && isFetching && currActiveSection !== activeSection) {
            return []; // para mostrar loading en mobile
        }
        return (
            orderedList ||
            (rules || []).map((item) => ({
                id: item.idRule,
                creationDate: item?.creationDate
                    ? moment(item.creationDate).tz("America/Argentina/Buenos_Aires")
                    : null,
                updatedDate: item?.updateDate ? moment(item.updateDate).tz("America/Argentina/Buenos_Aires") : null,
                number: item.idRule,
                name: item.ruleName,
                action: item.actionType,
                account: item.cbu && getAccountFromCBU(item.cbu),
                conditions: renderMapedConditions(item.conditions),
                status: i18n.get(`${FORM_ID}.card.status.${item.ruleState}.label`),
                hasErrors: item.ruleState === "ACTIVE" && item?.executionState === "ERROR",
                currency: item.currency,
                ...(rulesPrioritization && {
                    currencyLabel: item.currency
                        ? i18n.get(`${FORM_ID}.card.currency.${item.currency}`)
                        : i18n.get(`${FORM_ID}.card.currency.none`),
                }),
            }))
        );
    };

    const privateShellHomePath = getPostLoginHomePath(isUxDashboardImprovementsEnabled());

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push(privateShellHomePath));
        } else {
            dispatch(push("/desktop/"));
        }
    };

    const shellRightMobile = useMemo(() => {
        if (isDesktop || !embeddedInV3Shell || !activeEnvironment?.permissions?.[RULE_MANAGE_PERMISSION]) {
            return undefined;
        }
        return (
            <div
                className={classNames("toolbar-item", "toolbar-item-right", "mt-0", {
                    "toolbar-item-right-1": true,
                })}
                key="add-shell">
                <Button
                    onClick={() => dispatch(actions.ruleSummaryAction({ selectedRule: {}, action: "create" }))}
                    className={classNames({
                        "btn-outline": isDesktop,
                        "toolbar-btn view-close mx-0": !isDesktop,
                    })}>
                    <Image src="images/plus.svg" styles={{ color: "white" }} />
                    <I18n id="addLinkToLabel" componentProps={{ className: "visually-hidden" }} />
                </Button>
            </div>
        );
    }, [dispatch, isDesktop, embeddedInV3Shell, activeEnvironment]);

    useEffect(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["echeqRules"],
            titleKey: `${FORM_ID}.title`,
            goBackPath: privateShellHomePath,
            rightContentMobile: shellRightMobile,
        });
        return () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, privateShellHomePath, shellRightMobile]);

    const handleDownloadErrors = async () =>
        new Promise((resolve, reject) => {
            downloadErrorsEcheqRules("xls", idRuleSelected)
                .then(async ({ type, data }) => {
                    if (type === "W") {
                        dispatch(
                            notificationActions.showNotification(
                                i18n.get("global.unexpectedError"),
                                "error",
                                ["echeqRules"],
                                null,
                                false,
                            ),
                        );
                    } else {
                        const { content, fileName } = data.data;

                        downloadXls(fileName, content);
                        dispatch(
                            notificationActions.showNotification(
                                i18n.get("global.download.success"),
                                "success",
                                ["echeqRules"],
                                null,
                                false,
                            ),
                        );
                    }
                    resolve();
                })
                .catch(() => {
                    reject();
                });
        });

    const getExecutionHours = () => {
        const hours = configUtils.getArray("backend.daemon.echeqRules.hoursToExecute");
        let hoursToExecute = "";
        for (let i = 0; i < hours.length; i++) {
            const str = i === hours.length - 1 ? " y " : ", ";
            hoursToExecute += (i === 0 ? "" : str) + hours[i];
        }
        return { EXECUTION_HOURS: hoursToExecute };
    };

    const getHeader = () => {
        if (isDesktop) {
            return (
                <>
                    <Head onBack={handleBack} />
                    <Head
                        title={`${FORM_ID}.title`}
                        headerClassName="mt-3 mb-4"
                        hasInlineButtons
                        titleClassName="no-wrap"
                        messageTooltip="frontend.echeqRules.tooltip"
                        hintText={configUtils.get("frontend.echeqRules.tooltip", "", getExecutionHours())}
                        alwaysShowMessage
                        {...(activeEnvironment?.permissions?.[RULE_MANAGE_PERMISSION] && {
                            handleCreateClick: () => {
                                dispatch(actions.ruleSummaryAction({ selectedRule: {}, action: "create" }));
                            },
                            handleCreateMessage: `${FORM_ID}.newRule.label`,
                        })}
                    />
                </>
            );
        }
        if (embeddedInV3Shell) {
            return null;
        }
        return (
            <Head
                title={`${FORM_ID}.title`}
                headerClassName="blue-main-header-mobile blue-main-title-mobile mb-3"
                centerContentClassName="mx-5"
                onBack={handleBack}
                {...(activeEnvironment?.permissions?.[RULE_MANAGE_PERMISSION] && {
                    rightContent: () => (
                        <div
                            className={classNames("toolbar-item", "toolbar-item-right", "mt-0", {
                                "toolbar-item-right-1": true,
                            })}
                            key="add">
                            <Button
                                onClick={() =>
                                    dispatch(actions.ruleSummaryAction({ selectedRule: {}, action: "create" }))
                                }
                                className={classNames({
                                    "btn-outline": isDesktop,
                                    "toolbar-btn view-close mx-0": !isDesktop,
                                })}>
                                <Image src="images/plus.svg" styles={{ color: "white" }} />
                                <I18n id="addLinkToLabel" componentProps={{ className: "visually-hidden" }} />
                            </Button>
                        </div>
                    ),
                })}
            />
        );
    };

    const getNextPage = (pn) => {
        dispatch(actions.requestRulesList({ ...filters[activeSection], pageNumber: pn, activeSection }, isDesktop));
    };

    const getErrosNextPage = (pageNumber) => {
        const selectedRule = rules.find(({ idRule }) => idRule === idRuleSelected);
        dispatch(actions.ruleSummaryAction({ selectedRule, action: "seeErrors" }, pageNumber, isDesktop));
    };

    const showModal = (action) => {
        setTitleModalWarn(i18n.get(`${FORM_ID}.modal.${action}.title`));
        setDescriptionModalWarn(i18n.get(`${FORM_ID}.modal.${action}.description`));
        setIsModalWarnVisible(true);
    };

    const executeContextAction = (id, action) => {
        const selectedRule = rules.find(({ idRule }) => idRule === id);
        setActionSelected(action);
        setIdRuleSelected(id);
        switch (action) {
            case "disable":
            case "enable":
                showModal(action);
                break;
            case "delete":
            case "modify":
                if (selectedRule?.ruleState === "ACTIVE") {
                    showModal(action);
                } else {
                    dispatch(actions.ruleSummaryAction({ selectedRule, action }));
                }
                break;
            case "copy":
            case "seeErrors":
                dispatch(actions.ruleSummaryAction({ selectedRule, action }));
                break;
            default:
                break;
        }
    };

    const handleConfirmAction = () => {
        const selectedRule = rules.find(({ idRule }) => idRule === idRuleSelected);
        dispatch(actions.ruleSummaryAction({ selectedRule, action: actionSelected }));
        setIsModalWarnVisible(false);
    };

    const handleFormSubmit = useCallback(
        (filtersForm) => {
            const filtersActive = [];
            filtersActive[filtersForm.activeSection] = filtersForm;
            setOrderedList(null);
            dispatch(actions.setRememberedValues(filtersActive));
            dispatch(actions.requestRulesList(filtersForm, isDesktop));
        },
        [dispatch, isDesktop],
    );

    const isEnabledMVP = (mvp) => configUtils.getBoolean(`frontend.show.echeqRules.MVP${mvp}.functionalities`, false);

    const getContextMenuOptions = (action) => {
        switch (action) {
            case "seeConditions":
            case "disable":
            case "enable":
                return i18n.get(`${FORM_ID}.card.context.${action}.label`);
            case "seeErrors":
            case "copy":
                if (isEnabledMVP(2)) {
                    return i18n.get(`${FORM_ID}.card.context.${action}.label`);
                }
                return false;
            case "delete":
            case "modify":
                if (isEnabledMVP(3)) {
                    return i18n.get(`${FORM_ID}.card.context.${action}.label`);
                }
                return false;
            default:
                return false;
        }
    };

    const handleFormLabel = (id) => {
        const changeLabel = id === "issuer" && isEnabledSelectAll;
        const newLabel = changeLabel
            ? i18n.get(`${FORM_ID}.form.new.${id}.filters`)
            : i18n.get(`${FORM_ID}.form.${id}.filters`);
        return newLabel;
    };

    const handleOrderRules = (newList) => {
        setOrderedList(newList);
        const newOrderedList = newList.map((item, index) => ({
            id: item.number,
            order: index + 1,
        }));
        dispatch(actions.ruleSummaryAction({ selectedRule: {}, orderedList: newOrderedList, action: "reorder" }));
    };

    return (
        <>
            {!embeddedInV3Shell && (
                <Notification scopeToShow="echeqRules" notificationClassname={!isDesktop ? "z-index-9" : ""} />
            )}
            <div className="admin-detail-head mx-5">{getHeader()}</div>
            <div className={isDesktop ? "mx-5" : "mx-3"}>
                <EcheqRulesList
                    loading={isFetching}
                    emptyStateText={i18n.get(`${FORM_ID}.empty`)}
                    echeqsRulesArray={renderMapedRules()}
                    paginationProps={{ ...pagination, action: (pn) => getNextPage(pn) }}
                    getCardLabel={(key) => i18n.get(`${FORM_ID}.card.${key}.label`)}
                    getContextMenuLabel={getContextMenuOptions}
                    contextMenuAction={executeContextAction}
                    conditionsOptions={conditions}
                    operatorIssuerOptions={subConditionsIssuer}
                    operatorAmountOptions={subConditionsAmount}
                    operatorReceivedOptions={subConditionsReceived}
                    drawerConditionsText={(ruleName) =>
                        i18n.get(`${FORM_ID}.card.context.conditions.description`, "", { RULE_NAME: ruleName })
                    }
                    processText={processText}
                    hintText={configUtils.get("frontend.echeqRules.tooltip", "", getExecutionHours())}
                    showModalTyc={false}
                    enableContextMenu={activeEnvironment?.permissions?.[RULE_MANAGE_PERMISSION]}
                    titleModalEcheqsErrors={i18n.get(`${FORM_ID}.errorModal.title`, null, {
                        EXECUTION_DAY:
                            echeqsWithError?.length > 0
                                ? moment(echeqsWithError[0]?.executionDate).format("DD/MM/yyyy")
                                : "",
                        EXECUTION_HOUR:
                            echeqsWithError?.length > 0
                                ? moment(echeqsWithError[0]?.executionDate).format("HH:00")
                                : "",
                    })}
                    hintTextModalEcheqsErrors={configUtils.get("frontend.echeqRules.unprocessed.tooltip")}
                    echeqsWithErrorsList={echeqsWithError}
                    loadingEcheqsWithErrors={fetchingErrors}
                    handleDownloadEchqsWithErrors={handleDownloadErrors}
                    paginationPropsEchqsWithErrors={{ ...errorsPagination, action: (pn) => getErrosNextPage(pn) }}
                    getCardErrorsLabel={(id) => i18n.get(`${FORM_ID}.errorModal.${id}.label`)}
                    enableFilterByAdmit={isEnabledMVP(4)}
                    enableRulesPriority={rulesPrioritization}
                    onRulesOrderChange={handleOrderRules}
                    isProcessingRules={orderingRules}
                    {...(isEnabledMVP(3) && {
                        defaultSectionOption: activeSection,
                        enabledFilters: true,
                        getFormLabel: handleFormLabel,
                        validationSchema: Yup.object({}),
                        initialValues: {
                            ...INITIAL_STATE.filters,
                            "actives.inactives": {
                                ...INITIAL_STATE.filters["actives.inactives"],
                                ...getCreationDates(),
                            },
                        },
                        lastValuesRemembered: rememberedValues,
                        statusOptions: {
                            "actives.inactives": ["active", "inactive"].map((s) => ({
                                value: s,
                                label: i18n.get(`${FORM_ID}.form.status.${s}.filters`),
                            })),
                            "deleted.modified": ["deleted", "modified"].map((s) => ({
                                value: s,
                                label: i18n.get(`${FORM_ID}.form.status.${s}.filters`),
                            })),
                        },
                        orderByProps: {
                            propList: ["date"].map((s) => ({
                                value: s,
                                label: i18n.get(`${FORM_ID}.form.orderBy.${s}.filters`),
                            })),
                            defaultValueSelector: filters[activeSection]?.orderBy,
                            defaultValueRadioButtons: filters[activeSection]?.direction,
                            onChange: ({ orderBy, ...values }) => {
                                dispatch(
                                    actions.requestRulesList(
                                        {
                                            ...values,
                                            ...orderBy,
                                            totalPages: pagination?.totalPages,
                                            pageNumber: isDesktop ? pagination?.pageNumber : 1,
                                        },
                                        isDesktop,
                                    ),
                                );
                            },
                        },
                        onSubmit: handleFormSubmit,
                        onChageSectionOption: (option) => setCurrActiveSection(option),
                        minCreationDateFrom: getLimitDate(
                            -configUtils.getInteger("frontend.echeqRules.searchPeriod", 12),
                            new Date(),
                            "months",
                        ).toDate(),
                        maxCreationDateTo: moment().toDate(),
                    })}
                />
            </div>
            <ModalWarn
                isVisible={isModalWarnVisible}
                titleText={titleModalWarn}
                descriptionText={descriptionModalWarn}
                handleConfirm={() => handleConfirmAction()}
                handleCancel={() => setIsModalWarnVisible(false)}
            />
        </>
    );
}

EcheqRulesMain.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool,
    rules: arrayOf(shape({})),
    pagination: shape({}),
    isFetching: bool,
    fetchingErrors: bool,
    processText: string,
    activeEnvironment: shape({
        permissions: shape({}),
    }).isRequired,
    conditions: arrayOf(shape({})),
    subConditionsAmount: arrayOf(shape({})),
    subConditionsIssuer: arrayOf(shape({})),
    subConditionsReceived: arrayOf(shape({})),
    echeqsWithError: arrayOf(shape({})),
    errorsPagination: shape({}),
    activeSection: string.isRequired,
    filters: shape({}).isRequired,
    goBackEcheqsRule: bool.isRequired,
    orderingRules: bool,
    rememberedValues: shape({}),
    embeddedInV3Shell: bool,
};

EcheqRulesMain.defaultProps = {
    rules: [],
    conditions: [],
    subConditionsAmount: [],
    subConditionsIssuer: [],
    subConditionsReceived: [],
    echeqsWithError: [],
    isDesktop: true,
    isFetching: false,
    fetchingErrors: false,
    processText: null,
    pagination: {
        pageNumber: null,
        totalPages: null,
    },
    errorsPagination: {
        pageNumber: null,
        totalPages: null,
    },
    orderingRules: false,
    rememberedValues: null,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    isFetching: selectors.getFetchingList(state),
    pagination: selectors.getRulePagination(state),
    rules: selectors.getRuleList(state),
    processText: selectors.getProcessText(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    conditions: selectors.getConditions(state),
    subConditionsAmount: selectors.getSubConditionsAmount(state),
    subConditionsIssuer: selectors.getSubConditionsIssuer(state),
    subConditionsReceived: selectors.getSubConditionsReceived(state),
    echeqsWithError: selectors.getEcheqsWithError(state),
    fetchingErrors: selectors.getFetchingErrors(state),
    errorsPagination: selectors.getErrorsPagination(state),
    activeSection: selectors.getActiveSection(state),
    filters: selectors.getFilters(state),
    goBackEcheqsRule: echeqRuleSelectors.isGoBackEcheqsRule(state),
    orderingRules: selectors.getOrderingRules(state),
    rememberedValues: selectors.getRememberedValues(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqRulesMain);
